/**
 * Space weather sources (docs/specs/space-weather.md §3, §5): who provides each piece, how Teslyn credits it, the
 * public addresses the provider proxy asks for the keyless operations, the Bureau of Meteorology Space Weather API
 * request, and the readers that turn a provider's HTML, RSS or large JSON into small plain JSON on the server, so no
 * third-party HTML ever reaches a page (AC-4.1).
 *
 * MB's decisions: each organisation stores its own BoM Space Weather API key in Integrations and Teslyn Cloud asks
 * through the provider proxy every 15 minutes (decision 687, replacing the spec's first "one platform key"); staff
 * only, BoM credited with issue time and link (688); spaceweather.com's text shown with credit, link and a note that
 * its numbers are NOAA's (689); pc3 and GEOSTAT pictures kept, every 20 minutes (694); reading list from NOAA, BoM,
 * NASA DONKI and NASA, spaceweather.com and SpaceWeatherLive as plain links (693).
 *
 * No imports on purpose: the Edge Functions (Deno) use an identical copy, `supabase/functions/_shared/
 * space-weather-sources.ts` (tools/dev/copy-edge-shared.mjs); `test/integrations.test.ts` fails if the two differ.
 */

// ---------------------------------------------------------------------------------------------- who provides what

export type SpaceWeatherSourceId =
  | 'bom_sws'
  | 'noaa_swpc'
  | 'nasa_donki'
  | 'nasa'
  | 'spaceweather_com'
  | 'spaceweatherlive'
  | 'ga_intermagnet';

export interface SpaceWeatherSource {
  readonly id: SpaceWeatherSourceId;
  /** A short name for lists ("NOAA SWPC"). */
  readonly short: string;
  /** The credit line, as the provider asks to be named. */
  readonly credit: string;
  /** The provider's own home page (the credit's link). */
  readonly home: string;
  /** Hosts a link of this source may open (links outside them are dropped). */
  readonly hosts: readonly string[];
  /** The terms in one sentence, for the help page and hover text. */
  readonly terms: string;
}

export const SPACE_WEATHER_SOURCES: readonly SpaceWeatherSource[] = [
  {
    id: 'bom_sws',
    short: 'BoM',
    credit: 'Bureau of Meteorology Space Weather Services (ASWFC)',
    home: 'https://www.sws.bom.gov.au/',
    hosts: ['www.sws.bom.gov.au', 'sws.bom.gov.au'],
    terms:
      'Information presented with the permission of the Bureau of Meteorology (Australian Space Weather Forecasting Centre), Commonwealth of Australia; repackaging is Teslyn’s responsibility.',
  },
  {
    id: 'noaa_swpc',
    short: 'NOAA SWPC',
    credit: 'NOAA SWPC',
    home: 'https://www.spaceweather.gov/',
    hosts: ['www.spaceweather.gov', 'spaceweather.gov', 'services.swpc.noaa.gov', 'www.swpc.noaa.gov'],
    terms: 'US Government information, not subject to copyright protection; not endorsed by NOAA.',
  },
  {
    id: 'nasa_donki',
    short: 'NASA DONKI',
    credit: 'NASA CCMC DONKI',
    home: 'https://ccmc.gsfc.nasa.gov/tools/DONKI/',
    hosts: ['ccmc.gsfc.nasa.gov', 'kauai.ccmc.gsfc.nasa.gov', 'webtools.ccmc.gsfc.nasa.gov'],
    terms: 'NASA’s CCMC; model runs are research-quality estimates, not forecasts.',
  },
  {
    id: 'nasa',
    short: 'NASA',
    credit: 'NASA Solar Cycle 25 blog',
    home: 'https://science.nasa.gov/blogs/solar-cycle-25/',
    hosts: ['science.nasa.gov', 'www.nasa.gov'],
    terms: 'NASA content; no endorsement by NASA is implied.',
  },
  {
    id: 'spaceweather_com',
    short: 'Spaceweather.com',
    credit: 'Spaceweather.com',
    home: 'https://www.spaceweather.com/',
    hosts: ['www.spaceweather.com', 'spaceweather.com'],
    terms:
      'Shown with Spaceweather.com’s permission (decision 689); its solar wind numbers come from NOAA’s real-time solar wind.',
  },
  {
    id: 'spaceweatherlive',
    short: 'SpaceWeatherLive',
    credit: 'SpaceWeatherLive',
    home: 'https://www.spaceweatherlive.com/',
    hosts: ['www.spaceweatherlive.com'],
    terms: 'A link only: SpaceWeatherLive does not allow its graphs to be copied or linked to directly.',
  },
  {
    id: 'ga_intermagnet',
    short: 'Geoscience Australia',
    credit: 'Geoscience Australia observatories via INTERMAGNET (BGS)',
    home: 'https://www.intermagnet.org/',
    hosts: ['www.intermagnet.org', 'imag-data.bgs.ac.uk'],
    terms: 'A staff-only guide (decision 340): never exported, never in client PDFs or External screenshots.',
  },
];

export function spaceWeatherSource(id: SpaceWeatherSourceId): SpaceWeatherSource {
  return SPACE_WEATHER_SOURCES.find((s) => s.id === id)!;
}

/** The link if it is https on one of the source's own hosts, else null (links open in the system browser). */
export function sourceLink(id: SpaceWeatherSourceId, href: string): string | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' || u.username || u.password) return null;
  return spaceWeatherSource(id).hosts.includes(u.hostname.toLowerCase()) ? u.toString() : null;
}

// ---------------------------------------------------------------------------------------------- BoM Space Weather API

/** https://sws-data.sws.bom.gov.au/api-docs (read 26 Oct 2026): POST JSON `{ api_key, options }` to each method. */
export const BOM_SWS_API = 'https://sws-data.sws.bom.gov.au/api/v1/';

/** The `bom_sws` operations and the BoM method each one calls. */
export const BOM_SWS_METHODS = {
  k_index: 'get-k-index',
  a_index: 'get-a-index',
  dst_index: 'get-dst-index',
  mag_alert: 'get-mag-alert',
  mag_warning: 'get-mag-warning',
  aurora_alert: 'get-aurora-alert',
  aurora_watch: 'get-aurora-watch',
  aurora_outlook: 'get-aurora-outlook',
} as const;
export type BomSwsOp = keyof typeof BOM_SWS_METHODS;

/** The request of one `bom_sws` operation (`location` was checked by `checkOpParams`); null for an unknown op. */
export function bomSwsRequest(
  op: string,
  params: Readonly<Record<string, string>>,
  apiKey: string,
): { readonly url: string; readonly body: string } | null {
  const method = (BOM_SWS_METHODS as Record<string, string>)[op];
  if (!method) return null;
  const options: Record<string, string> =
    op === 'k_index'
      ? { location: params.location ?? 'Australian region' }
      : op === 'a_index' || op === 'dst_index'
        ? { location: 'Australian region' }
        : {};
  const body = Object.keys(options).length ? { api_key: apiKey, options } : { api_key: apiKey };
  return { url: `${BOM_SWS_API}${method}`, body: JSON.stringify(body) };
}

export interface BomSwsError {
  readonly code: number;
  readonly message: string;
}

/**
 * Read a BoM answer: `{ data: [...] }` or `{ errors: [{ code, message }] }`. Lenient about a trailing comma before
 * `]` or `}` (BoM's own published examples carry one); anything else that is not JSON is null.
 */
export function readBomSwsAnswer(
  text: string,
): { readonly data: readonly unknown[] } | { readonly errors: readonly BomSwsError[] } | null {
  const parsed = lenientJson(text);
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  if (Array.isArray(o.errors)) {
    return {
      errors: o.errors.map((e) => {
        const r = e && typeof e === 'object' ? (e as Record<string, unknown>) : {};
        return {
          code: typeof r.code === 'number' ? r.code : Number(r.code) || 0,
          message: typeof r.message === 'string' ? r.message : '',
        };
      }),
    };
  }
  if (Array.isArray(o.data)) return { data: o.data };
  return null;
}

/** BoM error codes that mean the stored key is wrong or missing (21 missing, 22 unsupported). */
export const BOM_KEY_REFUSED_CODES: readonly number[] = [21, 22];
/** BoM error code 23: the key is not allowed this data set. */
export const BOM_DATA_NOT_PERMITTED = 23;

/** JSON.parse, then once more without trailing commas; null when neither parses. */
export function lenientJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    try {
      return JSON.parse(text.replace(/,(\s*[\]}])/g, '$1')) as unknown;
    } catch {
      return null;
    }
  }
}

// ---------------------------------------------------------------------------------------------- keyless operations

export const NOAA_SWPC = 'https://services.swpc.noaa.gov';
export const DONKI_API = 'https://ccmc.gsfc.nasa.gov/DONKI-API/get/';
export const BOM_SWS_WEB = 'https://www.sws.bom.gov.au';

/** How the proxy turns the upstream answers of a keyless operation into its own answer. */
export type PublicOpReader =
  | 'json'
  | 'text'
  | 'solar_wind_now'
  | 'solar_wind_24h'
  | 'spaceweather_com'
  | 'bom_pictures'
  | 'bom_site_news'
  | 'swpc_news'
  | 'nasa_feed';

export interface PublicOpDef {
  readonly reader: PublicOpReader;
  /** The addresses asked, in order (DONKI's date range from the time of the call). */
  readonly urls: (nowMs: number) => readonly string[];
  /** Largest upstream answer accepted, bytes. */
  readonly maxBytes: number;
}

const day = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
const donki =
  (type: string, extra = ''): ((nowMs: number) => string[]) =>
  (nowMs) => [`${DONKI_API}${type}?startDate=${day(nowMs - 7 * 86_400_000)}&endDate=${day(nowMs)}${extra}`];
const fixed =
  (...u: string[]): (() => string[]) =>
  () =>
    u;
const MB = 1024 * 1024;

/**
 * The operations of the keyless provider `space_weather` (NOAA SWPC, NASA DONKI, spaceweather.com, BoM's public web
 * pictures and pages). Addresses are fixed here; no caller can steer the proxy elsewhere.
 */
export const PUBLIC_SPACE_WEATHER_OPS: Readonly<Record<string, PublicOpDef>> = {
  noaa_scales: { reader: 'json', urls: fixed(`${NOAA_SWPC}/products/noaa-scales.json`), maxBytes: MB },
  noaa_kp_1m: { reader: 'json', urls: fixed(`${NOAA_SWPC}/json/planetary_k_index_1m.json`), maxBytes: MB },
  noaa_alerts: { reader: 'json', urls: fixed(`${NOAA_SWPC}/products/alerts.json`), maxBytes: 2 * MB },
  noaa_solar_wind_now: {
    reader: 'solar_wind_now',
    urls: fixed(
      `${NOAA_SWPC}/products/summary/solar-wind-speed.json`,
      `${NOAA_SWPC}/products/summary/solar-wind-mag-field.json`,
    ),
    maxBytes: MB,
  },
  noaa_kp: { reader: 'json', urls: fixed(`${NOAA_SWPC}/products/noaa-planetary-k-index.json`), maxBytes: MB },
  noaa_kp_forecast: {
    reader: 'json',
    urls: fixed(`${NOAA_SWPC}/products/noaa-planetary-k-index-forecast.json`),
    maxBytes: MB,
  },
  noaa_forecast_3day: { reader: 'text', urls: fixed(`${NOAA_SWPC}/text/3-day-forecast.txt`), maxBytes: MB },
  noaa_geomag_forecast: {
    reader: 'text',
    urls: fixed(`${NOAA_SWPC}/text/3-day-geomag-forecast.txt`),
    maxBytes: MB,
  },
  noaa_discussion: { reader: 'text', urls: fixed(`${NOAA_SWPC}/text/discussion.txt`), maxBytes: MB },
  noaa_solar_wind_24h: {
    reader: 'solar_wind_24h',
    urls: fixed(`${NOAA_SWPC}/json/rtsw/rtsw_wind_1m.json`, `${NOAA_SWPC}/json/rtsw/rtsw_mag_1m.json`),
    maxBytes: 16 * MB,
  },
  noaa_enlil_frames: {
    reader: 'json',
    urls: fixed(`${NOAA_SWPC}/products/animations/enlil.json`),
    maxBytes: MB,
  },
  donki_enlil: { reader: 'json', urls: donki('WSAEnlilSimulations'), maxBytes: 4 * MB },
  donki_cme_analysis: {
    reader: 'json',
    urls: donki('CMEAnalysis', '&mostAccurateOnly=true'),
    maxBytes: 4 * MB,
  },
  donki_notifications: { reader: 'json', urls: donki('notifications', '&type=all'), maxBytes: 4 * MB },
  spaceweather_com: {
    reader: 'spaceweather_com',
    urls: fixed('https://www.spaceweather.com/'),
    maxBytes: 2 * MB,
  },
  bom_pictures: {
    reader: 'bom_pictures',
    urls: fixed(
      `${BOM_SWS_WEB}/Images/Geophysical/Latest%20Conditions/Maps/ico_pc3.gif`,
      `${BOM_SWS_WEB}/Images/Geophysical/Alerts%20and%20Warnings/GEOSTAT%20Alert/ico_geostat.gif`,
      `${BOM_SWS_WEB}/Geophysical/2/3`,
    ),
    maxBytes: MB,
  },
  bom_site_news: { reader: 'bom_site_news', urls: fixed(`${BOM_SWS_WEB}/Site_News`), maxBytes: MB },
  swpc_news: {
    reader: 'swpc_news',
    urls: fixed('https://www.spaceweather.gov/news-archive'),
    maxBytes: 2 * MB,
  },
  nasa_blog: {
    reader: 'nasa_feed',
    urls: fixed('https://science.nasa.gov/blogs/solar-cycle-25/feed/'),
    maxBytes: 2 * MB,
  },
};

/** What one upstream address answered (bytes; text readers decode them as UTF-8). */
export interface UpstreamBody {
  readonly url: string;
  readonly bytes: Uint8Array;
}

/**
 * The proxy's answer of keyless op `op` from the upstream bodies (one per address of `urls`, in order): plain JSON
 * or text, never HTML. Null when an answer is not what the source should send.
 */
export function readPublicOp(
  op: string,
  bodies: readonly UpstreamBody[],
): { readonly contentType: string; readonly body: string } | null {
  const def = PUBLIC_SPACE_WEATHER_OPS[op];
  if (!def || bodies.length !== def.urls(0).length) return null;
  const text = (i: number): string => utf8(bodies[i]!.bytes);
  const json = (v: unknown): { contentType: string; body: string } | null =>
    v === null ? null : { contentType: 'application/json', body: JSON.stringify(v) };
  switch (def.reader) {
    case 'json': {
      const t = text(0);
      const v = lenientJson(t);
      return v !== null && typeof v === 'object' ? { contentType: 'application/json', body: t } : null;
    }
    case 'text': {
      const t = text(0);
      return /<html|<!doctype/i.test(t.slice(0, 512))
        ? null
        : { contentType: 'text/plain; charset=utf-8', body: t };
    }
    case 'solar_wind_now': {
      const speed = lenientJson(text(0));
      const mag = lenientJson(text(1));
      return Array.isArray(speed) && Array.isArray(mag) ? json({ speed, mag }) : null;
    }
    case 'solar_wind_24h':
      return json(reduceRtsw(lenientJson(text(0)), lenientJson(text(1))));
    case 'spaceweather_com':
      return json(readSpaceweatherCom(text(0)));
    case 'bom_pictures': {
      const pc3 = gifDataUri(bodies[0]!.bytes);
      const geostatIcon = gifDataUri(bodies[1]!.bytes);
      const page = readBomGeostat(text(2));
      if (!pc3 && !geostatIcon && !page) return null;
      return json({ pc3_icon: pc3, geostat_icon: geostatIcon, geostat: page });
    }
    case 'bom_site_news':
      return json(readBomSiteNews(text(0)));
    case 'swpc_news':
      return json(readSwpcNews(text(0)));
    case 'nasa_feed':
      return json(readNasaFeed(text(0)));
  }
}

// ---------------------------------------------------------------------------------------------- readers

function utf8(b: Uint8Array): string {
  return new TextDecoder('utf-8', { fatal: false }).decode(b);
}

const ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  hellip: '…',
  deg: '°',
};

/** Tags removed and entities decoded; white space collapsed. Plain text only. */
export function plainText(html: string): string {
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
      if (e[0] === '#') {
        const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
        return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : ' ';
      }
      return ENTITIES[e.toLowerCase()] ?? m;
    })
    .replace(/[<>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** `data:image/gif;base64,…` of a GIF (checked by its signature), or null. At most 256 KB. */
export function gifDataUri(bytes: Uint8Array): string | null {
  if (bytes.length < 6 || bytes.length > 256 * 1024) return null;
  const sig = String.fromCharCode(...bytes.slice(0, 6));
  if (sig !== 'GIF87a' && sig !== 'GIF89a') return null;
  return `data:image/gif;base64,${base64(bytes)}`;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function base64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = i + 1 < bytes.length ? bytes[i + 1]! : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2]! : 0;
    const n = (a << 16) | (b << 8) | c;
    out += B64[(n >> 18) & 63]! + B64[(n >> 12) & 63]!;
    out += i + 1 < bytes.length ? B64[(n >> 6) & 63]! : '=';
    out += i + 2 < bytes.length ? B64[n & 63]! : '=';
  }
  return out;
}

export interface SpaceweatherComNow {
  readonly speed_km_s: number | null;
  readonly density_cm3: number | null;
  /** As the page says it ("Updated: Today at 0908 UT"). */
  readonly updated_text: string | null;
  /** The page's own date ("Monday, Oct. 26, 2026" → "2026-10-26"); null if absent. Older cached answers lack it. */
  readonly date?: string | null;
  /** The day's stories (R151, decision 689), in the page's order; older cached answers lack them. */
  readonly stories?: readonly SpaceweatherComStory[];
}

/** One of spaceweather.com's daily stories: its red headline and the first sentences after it, plain text. */
export interface SpaceweatherComStory {
  /** As the page writes it, without the closing colon ("A SPOTLESS DAY IS IN THE OFFING"). */
  readonly title: string;
  /** Up to two sentences of the story's first paragraph, at most `SWCOM_SUMMARY_MAX` characters. */
  readonly summary: string;
}

/** The most stories kept from one page, and the longest summary. */
export const SWCOM_STORIES_MAX = 5;
export const SWCOM_SUMMARY_MAX = 280;

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** The page's date line (`td.longDateText`: "Monday, Oct. 26, 2026") as an ISO date, or null. */
export function readSpaceweatherComDate(html: string): string | null {
  const m = /class="longDateText"[^>]*>([\s\S]{0,200}?)<\/td>/i.exec(html);
  if (!m) return null;
  const d = /([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),\s*(\d{4})/.exec(plainText(m[1]!));
  if (!d) return null;
  const month = MONTHS.indexOf(d[1]!.toLowerCase());
  const day = Number(d[2]);
  if (month < 0 || day < 1 || day > 31) return null;
  return `${d[3]}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** The first sentences of a story's text, up to `max` characters, cut at a sentence end where one fits. */
function storySummary(text: string, max = SWCOM_SUMMARY_MAX): string {
  // the plain text of a link ends in a space before its stop ("Space Baseball .")
  const tidy = text.replace(/\s+([.,!?:;])/g, '$1');
  const sentences = (tidy.match(/[^.!?]+[.!?]+(?=\s|$)/g) ?? []).map((s) => s.trim());
  let out = '';
  for (const s of sentences.slice(0, 2)) {
    const next = out ? `${out} ${s}` : s;
    if (next.length > max) break;
    out = next;
  }
  if (out) return out;
  return tidy.length > max ? `${tidy.slice(0, max - 1).trimEnd()}…` : tidy;
}

/**
 * spaceweather.com's daily stories (R151; decision 689: shown with credit, a link and a note that its numbers come
 * from NOAA). Each story starts a paragraph with a red bold headline ending in a colon (`<font color="#FF0000">
 * <strong>A SPOTLESS DAY IS IN THE OFFING:</strong></font> The sunspot number is …`), between the page's
 * `<!--news-->` mark and the rule under the stories. Plain text only; at most `SWCOM_STORIES_MAX`.
 */
export function readSpaceweatherComStories(html: string): SpaceweatherComStory[] {
  const start = html.indexOf('<!--news-->');
  if (start < 0) return [];
  const rest = html.slice(start, start + 120_000);
  const rule = rest.search(/<hr\b/i);
  const part = rule > 0 ? rest.slice(0, rule) : rest;
  const out: SpaceweatherComStory[] = [];
  const re =
    /<p\b[^>]*>\s*<font color=["']?#FF0000["']?>\s*<strong>([\s\S]*?)<\/strong>\s*<\/font>([\s\S]*?)<\/p>/gi;
  for (const m of part.matchAll(re)) {
    const title = plainText(m[1]!)
      .replace(/\s*:\s*$/, '')
      .trim();
    const text = plainText(m[2]!);
    if (!title || title.length > HEADLINE_MAX) continue;
    out.push({ title, summary: storySummary(text) });
    if (out.length >= SWCOM_STORIES_MAX) break;
  }
  return out;
}

/**
 * spaceweather.com's solar wind box (`span.solarWindText`, the first `span.solarWindUpdatedText`), its date line and
 * its daily stories; null if the page has none of them.
 */
export function readSpaceweatherCom(html: string): SpaceweatherComNow | null {
  const date = readSpaceweatherComDate(html);
  const stories = readSpaceweatherComStories(html);
  const m = /<span class="solarWindText"><b>Solar wind<\/b>([\s\S]*?)<\/span>/i.exec(html);
  if (!m) {
    return stories.length > 0
      ? { speed_km_s: null, density_cm3: null, updated_text: null, date, stories }
      : null;
  }
  const t = plainText(m[1]!);
  const num = (re: RegExp): number | null => {
    const x = re.exec(t);
    const v = x ? Number(x[1]) : NaN;
    return Number.isFinite(v) ? v : null;
  };
  const after = html.slice(m.index + m[0].length);
  const u = /<span class="solarWindUpdatedText">([\s\S]*?)<\/span>/i.exec(after);
  const updated = u ? plainText(u[1]!) : '';
  return {
    speed_km_s: num(/speed:\s*([\d.]+)\s*km\/sec/i),
    density_cm3: num(/density:\s*([\d.]+)\s*protons/i),
    updated_text: updated ? updated.slice(0, 80) : null,
    date,
    stories,
  };
}

export interface BomGeostat {
  /** The alert line ("NO ALERT CURRENT AT 0409 UT on 26 Oct 2026"), plain text. */
  readonly text: string;
  /** "26 Oct 2026 04:09 UT" from "(last updated …)". */
  readonly last_updated: string | null;
}

/** BoM's GEOSTAT Alert page (`/Geophysical/2/3`): the `file-text` paragraph and its "last updated". */
export function readBomGeostat(html: string): BomGeostat | null {
  const p = /<p class=['"]file-text['"]>([\s\S]*?)<\/p>/i.exec(html);
  if (!p) return null;
  const text = plainText(p[1]!).slice(0, 600);
  if (!text) return null;
  const u = /\(last updated ([^)]*)\)/i.exec(html);
  return { text, last_updated: u ? plainText(u[1]!) : null };
}

/** One reading-list item: headline, date, source, link. */
export interface ReadingItem {
  readonly source: SpaceWeatherSourceId;
  readonly title: string;
  /** ISO date or date-time (UTC), as the source gives it. */
  readonly date: string;
  readonly url: string;
  /** A sentence or two of the story, plain text, where the source allows it (spaceweather.com, decision 689). */
  readonly summary?: string;
}

const HEADLINE_MAX = 220;
const headline = (s: string): string =>
  s.length > HEADLINE_MAX ? `${s.slice(0, HEADLINE_MAX - 1).trimEnd()}…` : s;

/** BoM Site News (`<li><span class='news-time'>2026-09-13 – </span>text …`): newest first, at most 20. */
export function readBomSiteNews(html: string): ReadingItem[] {
  const out: ReadingItem[] = [];
  const re = /<li><span class=['"]news-time['"]>\s*(\d{4}-\d{2}-\d{2})[^<]*<\/span>([\s\S]*?)<\/li>/gi;
  for (const m of html.matchAll(re)) {
    const body = m[2]!;
    const title = plainText(body.replace(/<a[^>]*>\s*More\s*<\/a>/gi, ''))
      .replace(/\s*\[https?:[^\]]*\]/g, '')
      .trim();
    if (!title) continue;
    out.push({
      source: 'bom_sws',
      title: headline(title),
      date: m[1]!,
      url: `${BOM_SWS_WEB}/Site_News`,
    });
    if (out.length >= 20) break;
  }
  return out;
}

/** SWPC's news archive table (`views-field-title` link and the `<time datetime>` after it), at most 20. */
export function readSwpcNews(html: string): ReadingItem[] {
  const out: ReadingItem[] = [];
  const re =
    /<td class="views-field views-field-title"><a href="(\/news\/[A-Za-z0-9_-]+)"[^>]*>([\s\S]*?)<\/a>[\s\S]*?<time datetime="([^"]+)"/gi;
  for (const m of html.matchAll(re)) {
    const title = plainText(m[2]!);
    const date = /^\d{4}-\d{2}-\d{2}T[\d:]+([+-]\d{2}:\d{2}|Z)$/.test(m[3]!) ? m[3]! : '';
    if (!title || !date) continue;
    out.push({
      source: 'noaa_swpc',
      title: headline(title),
      date: new Date(date).toISOString(),
      url: `https://www.spaceweather.gov${m[1]!}`,
    });
    if (out.length >= 20) break;
  }
  return out;
}

/** An RSS 2.0 feed's items (NASA Solar Cycle 25 blog): title, pubDate, link on NASA's own hosts. */
export function readNasaFeed(xml: string): ReadingItem[] {
  const out: ReadingItem[] = [];
  for (const m of xml.matchAll(/<item>([\s\S]*?)<\/item>/gi)) {
    const it = m[1]!;
    const tag = (n: string): string => {
      const x = new RegExp(`<${n}>([\\s\\S]*?)</${n}>`, 'i').exec(it);
      return x ? plainText(x[1]!) : '';
    };
    const title = tag('title');
    const link = sourceLink('nasa', tag('link'));
    const when = Date.parse(tag('pubDate'));
    if (!title || !link || !Number.isFinite(when)) continue;
    out.push({ source: 'nasa', title: headline(title), date: new Date(when).toISOString(), url: link });
    if (out.length >= 20) break;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- solar wind 24 h

/** One 10-minute mean of the active spacecraft (null where it sent nothing usable). */
export interface SolarWindPoint {
  /** Start of the 10-minute bin (on the clock: :00, :10, … :50), ISO UTC. */
  readonly t: string;
  readonly speed: number | null;
  readonly density: number | null;
  readonly bt: number | null;
  readonly bz: number | null;
}

export interface SolarWind24h {
  /** The spacecraft marked `active: true` most often in the plasma rows (SOLAR1 on 26 Oct 2026). */
  readonly source: string | null;
  readonly points: readonly SolarWindPoint[];
}

/** The solar wind traces' means (MB, 27 Oct 2026: 10 minutes, was 5), bins on the clock. */
export const SOLAR_WIND_MEAN_MINUTES = 10;
const BIN_MS = SOLAR_WIND_MEAN_MINUTES * 60_000;

/**
 * NOAA's 1-minute real-time solar wind (`rtsw_wind_1m.json`, `rtsw_mag_1m.json`): only the rows marked `active`
 * (several spacecraft are in the files at once; SWPC moved from DSCOVR to SOLAR-1 in June 2026), reduced to 10-minute
 * means on the clock (:00, :10, … :50 UTC), oldest first (about 145 points a day). Null when neither file is a list.
 */
export function reduceRtsw(wind: unknown, mag: unknown): SolarWind24h | null {
  if (!Array.isArray(wind) && !Array.isArray(mag)) return null;
  type Acc = { s: number[]; d: number[]; bt: number[]; bz: number[] };
  const bins = new Map<number, Acc>();
  const sources = new Map<string, number>();
  const acc = (t: unknown): Acc | null => {
    const ms = typeof t === 'string' ? Date.parse(t.endsWith('Z') ? t : `${t}Z`) : NaN;
    if (!Number.isFinite(ms)) return null;
    const k = Math.floor(ms / BIN_MS) * BIN_MS;
    let a = bins.get(k);
    if (!a) bins.set(k, (a = { s: [], d: [], bt: [], bz: [] }));
    return a;
  };
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  for (const r of Array.isArray(wind) ? wind : []) {
    const o = r && typeof r === 'object' ? (r as Record<string, unknown>) : {};
    if (o.active !== true) continue;
    if (typeof o.source === 'string') sources.set(o.source, (sources.get(o.source) ?? 0) + 1);
    const a = acc(o.time_tag);
    if (!a) continue;
    const s = num(o.proton_speed);
    const d = num(o.proton_density);
    if (s !== null && s > 0 && s < 3000) a.s.push(s);
    if (d !== null && d >= 0 && d < 500) a.d.push(d);
  }
  for (const r of Array.isArray(mag) ? mag : []) {
    const o = r && typeof r === 'object' ? (r as Record<string, unknown>) : {};
    if (o.active !== true) continue;
    const a = acc(o.time_tag);
    if (!a) continue;
    const bt = num(o.bt);
    const bz = num(o.bz_gsm);
    if (bt !== null && bt >= 0 && bt < 500) a.bt.push(bt);
    if (bz !== null && Math.abs(bz) < 500) a.bz.push(bz);
  }
  const mean = (xs: number[]): number | null =>
    xs.length ? Math.round((xs.reduce((p, x) => p + x, 0) / xs.length) * 100) / 100 : null;
  const points = [...bins.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([k, a]) => ({
      t: new Date(k).toISOString(),
      speed: mean(a.s),
      density: mean(a.d),
      bt: mean(a.bt),
      bz: mean(a.bz),
    }));
  let source: string | null = null;
  let best = 0;
  for (const [s, n] of sources) if (n > best) [source, best] = [s, n];
  return { source, points };
}
